import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save, AlertTriangle, MessageCircle, CheckCircle2 } from 'lucide-react';

interface SuperAdminWhatsAppProps {
  db: any;
  getCollectionPath: (col: string) => string;
}

export function SuperAdminWhatsApp({ db, getCollectionPath }: SuperAdminWhatsAppProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    green_api_id_instance: '',
    green_api_token: '',
    template_confirmed: `*¡Turno Confirmado!* 🎾

Hola {{playerName}}, el club *{{clubName}}* te espera con la cancha lista.

📅 Fecha: {{date}}
⏰ Horario: {{timeSlot}} hs
📍 Cancha: {{courtName}}

📞 Teléfono del club: {{clubPhone}}
🔗 Ver info del club: {{clubUrl}}

*(🤖 Este es un mensaje automático, por favor comunícate directo al teléfono del club para consultas)*`,
    template_cancelled: `*Turno Cancelado* ⚠️

Hola {{playerName}}, lamentamos informarte que el club *{{clubName}}* ha cancelado tu reserva para el día {{date}} a las {{timeSlot}} hs en la {{courtName}}.

Por favor, comunícate con el club para reprogramar o recibir asistencia:
📞 Teléfono: {{clubPhone}}

*(🤖 Este es un mensaje automático)*`,
    template_pending: `🎾 *¡Nueva Solicitud de Reserva!*

👤 *Jugador:* {{playerName}}
📱 *Teléfono:* {{playerPhoneLink}}
📅 *Fecha:* {{date}}
⏰ *Horario:* {{timeSlot}}
📍 *Cancha:* {{courtName}}

Ingresa al sistema para aceptarla o rechazarla:
{{clubUrl}}`,
    template_reminder: `⏰ *¡Recordatorio de Turno!*

Hola {{playerName}}, te recordamos que tienes un turno programado en *{{clubName}}*.

📅 *Fecha:* {{date}}
⏰ *Horario:* {{timeSlot}}
📍 *Cancha:* {{courtName}}

¡Te esperamos!
📞 Teléfono del club: {{clubPhone}}

*(🤖 Este es un mensaje automático)*`,
    template_fixed_reminder: `⏰ *¡Consulta de Asistencia (Turno Fijo)!*

Hola {{playerName}}, tienes tu turno fijo programado en *{{clubName}}*.

📅 *Fecha:* {{date}}
⏰ *Horario:* {{timeSlot}}
📍 *Cancha:* {{courtName}}

Responde *SI* para confirmar tu asistencia o *NO* para cancelar la fecha de esta semana. 
Ante cualquier duda comunícate al: {{clubPhone}}

*(🤖 Este es un mensaje automático)*`
  });

  useEffect(() => {
    async function loadData() {
      try {
        const docRef = doc(db, getCollectionPath('settings'), 'whatsapp');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setFormData({
            green_api_id_instance: data.green_api_id_instance || '',
            green_api_token: data.green_api_token || '',
            template_confirmed: data.template_confirmed || formData.template_confirmed,
            template_cancelled: data.template_cancelled || formData.template_cancelled,
            template_pending: data.template_pending || formData.template_pending,
            template_reminder: data.template_reminder || formData.template_reminder,
            template_fixed_reminder: data.template_fixed_reminder || formData.template_fixed_reminder,
          });
        } else {
          // Si no existe el doc aún, vamos a guardarlo con los defaults y el token que dio el usuario
          setFormData(prev => ({
            ...prev,
            green_api_token: '59446249135b4dad8ec3895e5e959caad110c42e2874460595'
          }));
        }
      } catch (err) {
        console.error(err);
        setError('Error al cargar la configuración.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [db, getCollectionPath]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await setDoc(doc(db, getCollectionPath('settings'), 'whatsapp'), formData, { merge: true });
      setSuccess('Configuración guardada correctamente.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center animate-pulse text-white">Cargando configuración...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in slide-in-from-right-4">
      <form onSubmit={handleSave} className="bg-[#1a1c23] rounded-[var(--radius-large)] p-6 md:p-8 shadow-2xl relative space-y-8">
        
        <div className="flex items-center gap-4 border-b border-[#2d313f] pb-6">
          <div className="w-14 h-14 bg-[#25D366]/10 rounded-2xl flex items-center justify-center border border-[#25D366]/20">
            <MessageCircle size={28} className="text-[#25D366]" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-white tracking-tight uppercase">Master de WhatsApp</h2>
            <p className="text-sm text-zinc-400">Configuración global de Green API y plantillas de mensajes.</p>
          </div>
        </div>

        {/* CREDENCIALES GREEN API */}
        <div className="space-y-6">
          <h3 className="font-bold text-white uppercase tracking-wider text-sm flex items-center gap-2">
            <AlertTriangle className="text-yellow-500" size={16} /> Credenciales Globales
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#15171d] p-6 rounded-2xl border border-[#2d313f]">
            <div>
              <label className="block text-xs font-black uppercase tracking-wider text-zinc-400 mb-2 ml-1">ID de Instancia</label>
              <input
                type="text"
                placeholder="Ej: 7103456789"
                value={formData.green_api_id_instance}
                onChange={(e) => setFormData({...formData, green_api_id_instance: e.target.value})}
                className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3.5 focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] outline-none transition-all font-mono text-white text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs font-black uppercase tracking-wider text-zinc-400 mb-2 ml-1">Token de API</label>
              <input
                type="password"
                placeholder="Ej: 59446249..."
                value={formData.green_api_token}
                onChange={(e) => setFormData({...formData, green_api_token: e.target.value})}
                className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3.5 focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] outline-none transition-all font-mono text-white text-sm"
              />
            </div>
          </div>
        </div>

        {/* PLANTILLAS */}
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <h3 className="font-bold text-white uppercase tracking-wider text-sm">Plantillas de Mensajes</h3>
            <div className="bg-white/5 border border-white/10 px-3 py-2 rounded-lg text-[10px] text-zinc-400 font-mono">
              Variables: {'{{playerName}}, {{playerPhone}}, {{playerPhoneLink}}, {{clubName}}, {{date}}, {{timeSlot}}, {{courtName}}, {{clubPhone}}, {{clubUrl}}'}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div className="bg-[#15171d] p-6 rounded-2xl border border-[#2d313f]">
              <label className="block text-xs font-black uppercase tracking-wider text-[#25D366] mb-3">✅ Mensaje: Turno Confirmado</label>
              <textarea
                rows={10}
                value={formData.template_confirmed}
                onChange={(e) => setFormData({...formData, template_confirmed: e.target.value})}
                className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3.5 focus:border-[#25D366] outline-none transition-all font-mono text-white/90 text-sm leading-relaxed resize-y"
              />
            </div>

            <div className="bg-[#15171d] p-6 rounded-2xl border border-[#2d313f]">
              <label className="block text-xs font-black uppercase tracking-wider text-red-400 mb-3">❌ Mensaje: Turno Cancelado</label>
              <textarea
                rows={8}
                value={formData.template_cancelled}
                onChange={(e) => setFormData({...formData, template_cancelled: e.target.value})}
                className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3.5 focus:border-red-500 outline-none transition-all font-mono text-white/90 text-sm leading-relaxed resize-y"
              />
            </div>

            <div className="bg-[#15171d] p-6 rounded-2xl border border-[#2d313f]">
              <label className="block text-xs font-black uppercase tracking-wider text-blue-400 mb-3">🕒 Mensaje: Nueva Solicitud (al Club)</label>
              <textarea
                rows={8}
                value={formData.template_pending}
                onChange={(e) => setFormData({...formData, template_pending: e.target.value})}
                className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3.5 focus:border-blue-500 outline-none transition-all font-mono text-white/90 text-sm leading-relaxed resize-y"
              />
            </div>

            <div className="bg-[#15171d] p-6 rounded-2xl border border-[#2d313f]">
              <label className="block text-xs font-black uppercase tracking-wider text-purple-400 mb-3">⏰ Mensaje: Recordatorio de Asistencia</label>
              <textarea
                rows={8}
                value={formData.template_reminder}
                onChange={(e) => setFormData({...formData, template_reminder: e.target.value})}
                className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3.5 focus:border-purple-500 outline-none transition-all font-mono text-white/90 text-sm leading-relaxed resize-y"
              />
            </div>

            <div className="bg-[#15171d] p-6 rounded-2xl border border-[#2d313f]">
              <label className="block text-xs font-black uppercase tracking-wider text-orange-400 mb-3">🔁 Mensaje: Consulta Turno Fijo</label>
              <textarea
                rows={10}
                value={formData.template_fixed_reminder}
                onChange={(e) => setFormData({...formData, template_fixed_reminder: e.target.value})}
                className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-xl px-4 py-3.5 focus:border-orange-500 outline-none transition-all font-mono text-white/90 text-sm leading-relaxed resize-y"
              />
            </div>
          </div>
        </div>

        {/* ACCIONES */}
        <div className="pt-4 flex flex-col items-center gap-4 border-t border-[#2d313f]">
          {error && <p className="text-red-400 text-sm font-bold bg-red-500/10 px-6 py-3 rounded-xl border border-red-500/20">{error}</p>}
          {success && <p className="text-[#25D366] text-sm font-bold bg-[#25D366]/10 px-6 py-3 rounded-xl border border-[#25D366]/20 flex items-center gap-2"><CheckCircle2 size={16}/> {success}</p>}
          
          <button
            disabled={saving}
            type="submit"
            className="w-full md:w-auto min-w-[300px] bg-[#25D366] text-black font-black px-10 py-4 rounded-2xl transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-3 uppercase tracking-widest text-xs"
          >
            {saving ? (
              <div className="w-5 h-5 border-4 border-black/20 border-t-black rounded-full animate-spin" />
            ) : (
              <>
                <Save size={18} />
                Guardar Plantillas y Tokens
              </>
            )}
          </button>
        </div>

      </form>
    </div>
  );
}
